<template>
  <div class="shows-container">
    <transition name="zoom-move-fade" appear>
      <PhotoViewer
        :photo="selectedPhoto"
        v-if="selectedPhoto"
        @close="selectedPhoto = null"
        class="photo-viewer"
      ></PhotoViewer>
    </transition>

    <Transition name="modal-fade">
      <section v-if="upcoming.length" class="upcoming-shows">
        <h2 class="section-header load-hidden">Upcoming Shows</h2>
        <ShowCard
          v-for="show in upcoming"
          :key="show.title"
          :show="show"
          @close="selectedEvent = null"
          @poster="openPhotoViewer"
          class="show-card load-hidden"
          @mouseover="preloadImage(show.poster)"
        />
      </section>
    </Transition>

    <Transition name="modal-fade">
      <section v-if="past.length">
        <h2 class="section-header load-hidden">Past Shows</h2>
        <ShowCard
          v-for="show in past"
          :key="show.title"
          :show="show"
          @close="selectedEvent = null"
          @poster="openPhotoViewer"
          class="show-card load-hidden"
        />
      </section>
    </Transition>
  </div>
</template>

<script>
import shows from '@/assets/data/shows.json';
import ShowCard from '@/components/shows/ShowCard.vue';
import PhotoViewer from '@/components/media/PhotoViewer.vue';
import ScrollReveal from 'scrollreveal';
import { useHead } from '@vueuse/head';

export default {
  name: 'TourView',
  components: { ShowCard, PhotoViewer },

  mounted() {
    useHead({
      title: 'Tour | NightKill Official',
      meta: [
        {
          name: 'description',
          content:
            'Catch NightKill live — melodic heavy metal from Greece. Upcoming tour dates and past show history.',
        },
        {
          name: 'keywords',
          content:
            'NightKill tour dates, live shows, concert metal band, Greek heavy metal gigs, band tour info',
        },
        { property: 'og:title', content: 'Tour | NightKill Official' },
        {
          property: 'og:description',
          content:
            'Explore NightKill’s tour schedule — melodic heavy metal from Greece. See where we’re playing next.',
        },
        {
          property: 'og:image',
          content: 'https://nightkill.gr/metadata/nightkill-logo.png',
        },
        {
          property: 'og:url',
          content: 'https://nightkill.gr/tour',
        },
        { property: 'og:type', content: 'website' },
        { property: 'og:site_name', content: 'NightKill Official' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Tour | NightKill Official' },
        {
          name: 'twitter:description',
          content:
            'Tour updates from NightKill — check out upcoming concerts and past gigs from Greece’s melodic metal band.',
        },
        {
          name: 'twitter:image',
          content: 'https://nightkill.gr/metadata/nightkill-logo.png',
        },
      ],
      link: [{ rel: 'canonical', href: 'https://nightkill.gr/tour' }],

      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': shows
              .filter((show) => new Date(show.date) >= new Date())
              .map((show) => ({
                '@type': 'MusicEvent',
                name: show.title,
                startDate: show.date,
                image: `https://nightkill.gr/media/images/shows/${show.poster}`,
                location: {
                  '@type': 'Place',
                  name: show.venue,
                  address: {
                    '@type': 'PostalAddress',
                    addressLocality: show.location,
                    addressCountry: 'GR',
                  },
                },
                performer: {
                  '@type': 'MusicGroup',
                  name: 'NightKill',
                },
                eventStatus: 'https://schema.org/EventScheduled',
              })),
          }),
        },
      ],
    });

    this.$nextTick(() => {
      setTimeout(() => {
        const sr = ScrollReveal({
          easing: 'ease-out',
          cleanup: true,
        });

        sr.reveal('.section-header', {
          origin: 'left',
          distance: '20px',
          duration: 400,
        });

        sr.reveal('.show-card', {
          origin: 'bottom',
          distance: '40px',
          duration: 700,
          delay: 200,
          interval: 100,
          scale: 0.95,
        });
      }, 100);
    });
  },
  beforeUnmount() {
    ScrollReveal().destroy();
  },
  computed: {
    upcoming() {
      const now = new Date();
      return shows
        .filter((show) => new Date(show.date) >= now)
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // Descending
    },
    past() {
      const now = new Date();
      return shows
        .filter((show) => new Date(show.date) < now)
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // Descending
    },
  },

  methods: {
    openPhotoViewer(photo) {
      this.selectedPhoto = `/media/images/shows/${photo}`;
    },
    preloadImage(path) {
      const image = new Image();
      image.src = `/media/images/shows/${path}`;
    },
  },

  data() {
    return {
      selectedPhoto: null,
    };
  },
};
</script>

<style scoped>
.shows-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  margin: 2rem auto;
  padding: 0 1rem;
  gap: 2rem;
  margin-top: 0;
}

.upcoming-shows {
  margin-bottom: 7rem;
}

h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: #2c3e50;
  margin-top: 0;
}
section {
  width: 100%;
}

/* Fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.5s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.modal-fade-enter-to,
.modal-fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

/* Zoom + Move + Fade Transition */
.zoom-move-fade-enter-active,
.zoom-move-fade-leave-active {
  transition: all 0.45s ease;
}

.zoom-move-fade-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(30px);
}

.zoom-move-fade-enter-to {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.zoom-move-fade-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

.zoom-move-fade-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(30px);
}
</style>
